// src/components/ClaimantChannelPanel.js
//
// T5 item 8 (2026-10-15) - RULING 29: THE CLAIMANT'S ALWAYS-OPEN CHANNEL, on the claim page.
// The claimant sends a request, a question or a complaint; the office holding the claim answers it. Every item shows
// its type, who it went to, and - while open - how many days it has waited. The rules and routing are
// src\data\claimantChannel.js; this file renders and calls the channelItem function, and decides nothing itself.
import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CHANNEL_TYPES, TYPE_LABELS, TEXT_MAX, COMPLAINT, INSTRUCTION, HOW_GIVEN, channelClaimant, holdingOffice, mayAnswerItem, mayCapture, openAgeDays, routeItem } from '../data/claimantChannel';
import { factsCaller } from './FactsActsPanel';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', accent: '#E8A020', ok: '#5FB878', bad: '#E06C6C' };
const OFFICE = { lc: 'the Local Chairperson', gc: 'the General Chairperson' };
const AUD = { local_chair: 'Local Chairperson', general_chair: 'General Chairperson', claimant: 'the claimant' };
// The caller as both tests read him: the office test (role, committee, scopes) and the claimant test (uid, email).
function channelCaller(user) {
  const u = user || {};
  return { ...factsCaller(u), committee: u.committee, scopes: u.scopes || [], isAdmin: !!u.isAdmin };
}

async function callChannel(payload) {
  const fn = httpsCallable(getFunctions(), 'channelItem');
  const r = await fn(payload);
  return r.data;
}

function whenOf(t) {
  const d = t && typeof t.toDate === 'function' ? t.toDate() : null;
  return d ? d.toLocaleDateString() : 'date pending';
}

function ItemRow({ x, claim, user, claimId }) {
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const age = openAgeDays(x);
  const officer = mayAnswerItem(claim, channelCaller(user), x).may;   // T5 item 9: the claimant answers an instruction
  const instr = x.type === INSTRUCTION;
  const send = async () => {
    setBusy(true); setErr('');
    try { await callChannel({ claimId, itemId: x.id, answer }); setAnswer(''); }
    catch (e) { setErr((e && e.message) || 'The answer was not recorded.'); }
    setBusy(false);
  };
  return (
    <div style={{ padding: '8px 0', borderBottom: '1px dotted ' + C.line }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
        <span style={{ color: x.type === COMPLAINT ? C.bad : C.text, fontWeight: 700 }}>
          {TYPE_LABELS[x.type] || x.type} <span style={{ color: C.dim, fontWeight: 400 }}>- {instr ? 'given ' + (HOW_GIVEN[x.how] || '') + ' on ' + x.givenOn + ', recorded by ' + (x.byName || 'the representative') + ' ' + whenOf(x.at) : whenOf(x.at) + ' - to ' + ((x.routedTo || []).map(a => AUD[a] || a).join(' and ') || 'no office')}</span>
        </span>
        <span style={{ whiteSpace: 'nowrap', color: x.status === 'answered' ? C.ok : C.accent }}>
          {x.status === 'answered' ? (instr ? 'answered by the claimant' : 'answered') : instr ? 'waiting on the claimant' : age === null ? 'open' : 'open ' + age + 'd'}
        </span>
      </div>
      <div style={{ color: C.text, fontSize: 12, whiteSpace: 'pre-wrap', marginTop: 4 }}>{x.text}</div>
      {x.status === 'answered' ? (
        <div style={{ marginTop: 6, paddingLeft: 10, borderLeft: '2px solid ' + C.ok, fontSize: 12, color: C.text, whiteSpace: 'pre-wrap' }}>
          <div style={{ color: C.dim, fontSize: 11 }}>Answered by {x.answeredBy || 'the office'} - {whenOf(x.answeredAt)}</div>
          {x.answer}
        </div>
      ) : officer ? (
        <div style={{ marginTop: 6 }}>
          <textarea aria-label="Your answer to the instruction" className="form-input" rows={2} maxLength={TEXT_MAX} value={answer} onChange={e => setAnswer(e.target.value)} placeholder={instr ? 'Confirm it, or say what you actually said' : 'Your answer to the claimant'} style={{ width: '100%' }} />
          <button className="btn btn-secondary" disabled={busy || !answer.trim()} onClick={send} style={{ marginTop: 4 }}>{busy ? 'Recording...' : instr ? 'Record my answer' : 'Record the answer'}</button>
          {err ? <div style={{ color: C.bad, fontSize: 11 }}>{err}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

// T5 item 9 (ruling 139): the office records the member's verbal instruction; the platform shows it to him at once.
function InstructionCapture({ claimId }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ text: '', givenOn: '', how: 'phone' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      await callChannel({ claimId, type: INSTRUCTION, text: f.text, givenOn: f.givenOn, how: f.how });
      setF({ text: '', givenOn: '', how: 'phone' }); setMsg({ ok: true, text: 'Recorded, and shown to the claimant now. He confirms it or says what he actually said.' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };
  return (
    <div style={{ marginBottom: 10 }}>
      <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => setOpen(!open)}>{open ? 'Close' : 'Record an instruction the member gave you verbally'}</button>
      {open ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, color: C.accent, marginBottom: 4 }}>The member sees this the moment you record it, with your name. Write what he told you, as he said it.</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            <input aria-label="Given on" className="form-input" type="date" value={f.givenOn} onChange={e => setF({ ...f, givenOn: e.target.value })} title="The day he gave it" />
            <select aria-label="How the instruction was given to you" className="form-select" value={f.how} onChange={e => setF({ ...f, how: e.target.value })}>{Object.entries(HOW_GIVEN).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <textarea aria-label="The instruction, as the member gave it" className="form-input" rows={3} maxLength={TEXT_MAX} value={f.text} onChange={e => setF({ ...f, text: e.target.value })} placeholder="The instruction, as the member gave it" style={{ width: '100%' }} />
          <button className="btn btn-primary" disabled={busy || !f.text.trim() || !f.givenOn} onClick={save} style={{ marginTop: 4 }}>{busy ? 'Recording...' : 'Record and show it to the member'}</button>
          {msg ? <div style={{ fontSize: 12, marginTop: 4, color: msg.ok ? C.ok : C.bad }}>{msg.text}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

export default function ClaimantChannelPanel({ claim, claimId, user }) {
  const [items, setItems] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [type, setType] = useState('question');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!claimId) return undefined;
    const q = query(collection(db, 'claims', claimId, 'channelItems'), orderBy('at', 'desc'));
    return onSnapshot(q, snap => { setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoadErr(''); },
      e => setLoadErr((e && (e.code || e.message)) || 'refused'));
  }, [claimId]);

  const held = holdingOffice(claim);
  if (!held) return null;   // a draft: the channel opens once the claim is filed
  const isClaimant = channelClaimant(claim, factsCaller(user)).may;
  const captures = mayCapture(claim, channelCaller(user)).may;   // T5 item 9 (ruling 139)
  const route = routeItem(claim, type);
  const open = items.filter(x => x.status === 'open').length;

  const post = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await callChannel({ claimId, type, text });
      setText('');
      setMsg({ ok: true, text: 'Recorded and sent to ' + (r.routedTo || []).map(a => AUD[a] || a).join(' and ') + '.'
        + (r.aboveNotOnPlatform ? ' The office above the General Chairperson is not on this platform, so this complaint went no higher than the General Chairperson.' : '')
        + ((r.unaddressed || []).length ? ' Not addressed: ' + r.unaddressed.join('; ') + '.' : '') });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>
        The claimant's channel {open ? <span style={{ color: C.accent }}>- {open} open</span> : null}
      </div>
      <div style={{ color: C.dim, fontSize: 11, margin: '2px 0 8px' }}>
        Held now by {OFFICE[held]}. A request or question goes to the office holding the claim; a complaint also goes one level above it. Nothing here is ever deleted.
      </div>
      {isClaimant ? (
        <div style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            {CHANNEL_TYPES.map(t => (
              <label key={t} style={{ fontSize: 12, color: C.text }}>
                <input aria-label={TYPE_LABELS[t]} type="radio" checked={type === t} onChange={() => setType(t)} /> {TYPE_LABELS[t]}
              </label>
            ))}
          </div>
          <textarea aria-label="What you want recorded" className="form-input" rows={3} maxLength={TEXT_MAX} value={text} onChange={e => setText(e.target.value)}
                    placeholder={type === COMPLAINT ? 'What is wrong with how your claim is being handled' : 'What you are asking'} style={{ width: '100%' }} />
          <div style={{ fontSize: 11, color: C.dim }}>
            Goes to: {route.routes.map(r => AUD[r.audience]).join(' and ') || 'no office'}
            {route.aboveNotOnPlatform ? ' (the office above the General Chairperson is not on this platform)' : ''}
          </div>
          <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={post} style={{ marginTop: 6 }}>
            {busy ? 'Sending...' : 'Send ' + TYPE_LABELS[type].toLowerCase()}
          </button>
          {msg ? <div style={{ fontSize: 12, marginTop: 4, color: msg.ok ? C.ok : C.bad }}>{msg.text}</div> : null}
        </div>
      ) : null}
      {captures ? <InstructionCapture claimId={claimId} /> : null}
      {loadErr ? <div style={{ color: C.bad, fontSize: 12 }}>The channel could not be loaded ({loadErr}). This is a load failure, not an empty channel.</div>
        : items.length === 0 ? <div style={{ color: C.dim, fontSize: 12 }}>Nothing has been sent on this claim's channel.</div>
        : items.map(x => <ItemRow key={x.id} x={x} claim={claim} user={user} claimId={claimId} />)}
    </div>
  );
}
